import React from 'react';
import { css } from '@emotion/react';
import { CountdownCircleTimer } from 'react-countdown-circle-timer';

import { PlayerMode } from '$/components/WebinarPlayer/types';
import { withWebinarPlayerContext } from '$/components/WebinarPlayer/context';

import { isMobile } from '$/utils/isMobile';
import { useHasChangedShallow } from '$/hooks/useHasChanged';
import formatTimeInWebinarSecs from '$/utils/formatTimeInWebinarSecs';

import { PlayButton } from './PlayButton';
import { PlayButtonSizePx } from './PlayButtonSizePx';

export const TimedPlayButton: React.FC<{
  duration: number;
  message?: string;
  remainingTime?: number | null;
  onClick: React.MouseEventHandler<HTMLDivElement | HTMLButtonElement>;
  hideTimer?: boolean;
  className?: string;
}> = withWebinarPlayerContext(
  ['setIsPlaying', 'isPlaying', 'ewebinar', 'mode', 'isInBackground', 'interactionInEditModalId'],
  'TimedPlayButton'
)(({ duration, message, remainingTime, onClick, className, context }) => {
  const { setIsPlaying, isPlaying, ewebinar, mode, isInBackground, interactionInEditModalId } =
    context;
  const isInBackgroundRef = React.useRef(isInBackground);
  const isPlayingRef = React.useRef(isPlaying);
  const timeout = React.useRef<ReturnType<typeof setTimeout> | null>(null);
  const interactionInEditModalIdRef = React.useRef(interactionInEditModalId);

  const durationChanged = useHasChangedShallow([duration]);

  React.useEffect(() => {
    interactionInEditModalIdRef.current = interactionInEditModalId;
  }, [interactionInEditModalId]);

  React.useEffect(() => {
    isInBackgroundRef.current = isInBackground;
  }, [isInBackground]);

  React.useEffect(() => {
    isPlayingRef.current = isPlaying;
  }, [isPlaying]);

  React.useEffect(() => {
    if (!durationChanged) return;
    if (duration) {
      timeout.current = setTimeout(() => {
        if (
          isInBackgroundRef.current &&
          !isPlayingRef.current &&
          !interactionInEditModalIdRef.current
        ) {
          setIsPlaying(true);
        }
      }, duration * 1000);
    } else {
      if (timeout.current) clearTimeout(timeout.current);
    }
  }, [duration, durationChanged, interactionInEditModalId, setIsPlaying]);

  React.useEffect(() => {
    return (): void => {
      timeout.current && clearTimeout(timeout.current);
    };
  }, []);

  const fontSizeRem = isMobile ? 1.5 : 2;
  const borderWidth = 5;
  const buttonSizePx = PlayButtonSizePx;

  const renderTime = React.useCallback(
    ({ remainingTime }: { remainingTime: number }): JSX.Element => {
      return (
        <>
          {message ? (
            <div
              css={css`
                position: absolute;
                left: 50%;
                width: 700%;
                max-width: 100vw;
                text-align: center;
                line-height: 1.25;
                transform: translate(-50%, 0);
                bottom: calc(100% + ${fontSizeRem}rem);
                font-size: ${fontSizeRem}rem;
              `}
            >
              {message}
            </div>
          ) : null}
          <PlayButton
            invert
            css={css`
              border-width: ${borderWidth}px;
              border-color: rgba(0, 0, 0, 0) !important;
            `}
            onClick={onClick}
          />
          <div
            className='flex flex-col justify-end items-center'
            css={css`
              position: absolute;
              top: 0;
              left: 0;
              right: 0;
              bottom: -${1 + fontSizeRem}rem;
              font-size: ${fontSizeRem}rem;
            `}
          >
            <div className='font-mono' key={remainingTime}>
              {formatTimeInWebinarSecs(remainingTime)}
            </div>
          </div>
        </>
      );
    },
    [fontSizeRem, message, onClick]
  );

  if (!isPlaying && duration) {
    return (
      <div
        className={`relative z-player-control scale-control aspect-scale appearence-none w-full h-full flex flex-col items-center justify-center outline-none ${className}`}
        css={css`
          pointer-events: none;
        `}
      >
        <CountdownCircleTimer
          isPlaying={true}
          duration={duration}
          initialRemainingTime={remainingTime ?? undefined}
          strokeWidth={borderWidth}
          trailStrokeWidth={borderWidth}
          strokeLinecap='square'
          size={buttonSizePx}
          rotation='counterclockwise'
          colors={['#fff', '#fff']}
          trailColor={'#ffffff40'}
          colorsTime={[duration, 0]}
          onComplete={(): { shouldRepeat: boolean; delay: number } => {
            if (!interactionInEditModalId) {
              setIsPlaying(true);
            }
            return { shouldRepeat: false, delay: 1 };
          }}
          updateInterval={0.2}
        >
          {renderTime}
        </CountdownCircleTimer>
      </div>
    );
  }

  if (mode !== PlayerMode.replay && !ewebinar.viewerRoomSettings.enableVideoPause) {
    return null;
  }

  return (
    <div className={`relative control ${className}`}>
      <PlayButton
        invert
        css={css`
          height: ${buttonSizePx}px;
          width: ${buttonSizePx}px;
        `}
        onClick={onClick}
      />
    </div>
  );
});
